import toast, { Toaster } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import GatesConfigTable from "../../../components/table/GatesConfigTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import useAuthStore from "../../../store/Auth";
import { PAGE_SIZE } from "../../../constants/common";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { GatesSchema } from "../../../schemas/gatesSchema";
import {
  useGetAllBuildingsById,
  useGetAllGates,
  useGetAllLocationsDropdown,
  useGetGateById,
} from "../../../hooks/common/query";
import { Gate } from "../../../hooks/common/interface";
import { Controller } from "react-hook-form";
import TextInput from "../../../components/inputs/TextInput";
import { Button } from "../../../components/button";
import ComboboxInput from "../../../components/inputs/ComboboxInput";
import { mapToOptions } from "../../../lib/functions";
import {
  useAddUpdateGate,
  useIsGateExists,
} from "../../../hooks/common/mutation";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";

interface GatesConfigData {
  gateNameEn: string;
  gateNameAr: string;
  location: string | null;
  building: string | null;
}

const GatesConfig = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
    watch,
  } = useFormWithSchema(GatesSchema);

  const [allGates, setAllGates] = useState<Gate[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [selectedGateId, setSelectedGateId] = useState(0);

  const { mutateAsync: IsGateExists } = useIsGateExists();
  const { data: gates } = useGetAllGates({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  const { data: allLocations } = useGetAllLocationsDropdown();

  const selectedLocationId = watch("location");

  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: selectedLocationId ? selectedLocationId.toString() : "",
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings
          )
        : [],
    [allBuildings]
  );

  useEffect(() => {
    if (gates) {
      setAllGates(gates.data || []);
      setPages(gates.pagination?.pages || 1);
      setCount(gates.pagination?.count || 0);
    }
  }, [setPages, gates]);

  const { mutateAsync: AddUpdateGate, isPending: isAddUpdateGateLoading } =
    useAddUpdateGate();

  const user = useAuthStore((state) => state.user);

  const { data: selectedGate } = useGetGateById({
    encryptedId: selectedGateId ? selectedGateId.toString() : "",
  });

  useEffect(() => {
    if (selectedGate) {
      setValue("gateNameEn", selectedGate.s_GateName_En);
      setValue("gateNameAr", selectedGate.s_GateName_Ar);
      setValue("location", selectedGate.n_LocationID?.toString());
      setValue("building", selectedGate.n_BuildingID?.toString());
    }
  }, [setValue, selectedGate]);

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations
      ),
    [allLocations]
  );

  const onSubmit = useCallback(
    async (data: GatesConfigData) => {
      try {
        const isExistBody = {
          n_GateID: selectedGateId,
          s_GateName_Ar: data.gateNameAr,
          s_GateName_En: data.gateNameEn,
          n_LocationID: Number(data.location),
          n_BuildingID: Number(data.building),
        };
        const { isExists } = await IsGateExists(isExistBody);

        if (isExists) {
          toast.error(t("gateExists"));
          return;
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error checking if gate exists", error);
        return;
      }

      try {
        if (user?.user_id) {
          const body = {
            n_GateID: 0,
            n_LocationID: Number(data.location),
            n_BuildingID: Number(data.building),
            s_GateName_Ar: data.gateNameAr,
            s_GateName_En: data.gateNameEn,
          };

          if (selectedGateId === 0) {
            await AddUpdateGate(body);
          } else {
            const updateBody = {
              ...body,
              n_GateID: selectedGateId,
            };

            await AddUpdateGate(updateBody);
          }
        }

        setSelectedGateId(0);

        if (selectedGateId === 0) {
          toast.success(t("gateAdded"));
        } else {
          toast.success(t("gateUpdated"));
        }

        resetField("gateNameAr");
        resetField("gateNameEn");
        resetField("location");
        resetField("building");
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error adding/updating gate", error);
      }
    },
    [AddUpdateGate, IsGateExists, resetField, selectedGateId, t, user?.user_id]
  );

  const onClear = useCallback(() => {
    setSelectedGateId(0);

    resetField("gateNameAr");
    resetField("gateNameEn");
    resetField("location");
    resetField("building");
  }, [resetField]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("gatesConfig")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl grid sm:grid-cols-2 md:grid-cols-4 gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
            <Controller
              name="gateNameEn"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("gateNameEnglish")}
                  onChange={onChange}
                  value={value || ""}
                  error={errors?.[name]?.message}
                  required
                />
              )}
            />

            <Controller
              name="gateNameAr"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("gateNameArabic")}
                  onChange={onChange}
                  value={value || ""}
                  required
                  error={errors?.[name]?.message}
                />
              )}
            />

            <Controller
              name="location"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={locations}
                  value={
                    value
                      ? locations?.find((item) => item.value === Number(value))
                          ?.labelEn || ""
                      : ""
                  }
                  onChange={(value) => {
                    onChange(value);
                    setValue("building", "");
                  }}
                  error={errors?.[name]?.message}
                  label={t("location")}
                  required
                />
              )}
            />

            <Controller
              name="building"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <ComboboxInput
                  options={buildings}
                  value={
                    buildings?.find((item) => item.value === Number(value))
                      ?.labelEn || ""
                  }
                  required
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  label={t("building")}
                />
              )}
            />

            <div className="flex items-center gap-2 mt-1">
              <Button isLoading={isAddUpdateGateLoading}>{t("save")}</Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>
          <GatesConfigTable
            gates={allGates}
            setSelectedGateId={setSelectedGateId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>
    </>
  );
};

export default GatesConfig;
